import { useAuth } from "@clerk/clerk-react";
import { Box, Button, IconButton, TextField, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { DiscoverTabView, DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { UserRequestsContext } from "contexts/UserRequests";
import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  CombinedRequest,
  RequestStatus,
  SavedBrandStatus,
} from "schemas/dashboard";

import { fetcherAuth } from "utils/api";

import LoadingScreen from "./LoadingScreen";
import { PreviewLine, UploadResult } from "./schema";
import styles from "./styles";

const PLACEHOLDER = `@glossier
olaplex.com,
Fenty Beauty, hello@fentybeauty.com
https://www.supplylittle.com
"Ben & Jerry's, Inc."
@drunkelephant, info@drunkelephant.com`;

const PREVIEW_DEBOUNCE_MS = 400;
const ISSUES_PREVIEW_COUNT = 3;

interface Props {
  collectionName: string;
  setCollectionName: (value: string) => void;
  onUploaded: (result: UploadResult) => void | Promise<void>;
  onClose?: () => void;
}

const parseLines = (text: string): string[] =>
  text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const UploadForm = ({
  collectionName,
  setCollectionName,
  onUploaded,
  onClose,
}: Props) => {
  const { getToken } = useAuth();
  const { setDiscoverTab } = useContext(DiscoverViewContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { setErrorAlert } = useContext(AlertContext);
  const { userRequests, setUserRequests, setTotal } =
    useContext(UserRequestsContext);

  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [preview, setPreview] = useState<PreviewLine[]>([]);
  const [issuesExpanded, setIssuesExpanded] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const lines = useMemo(() => parseLines(text), [text]);
  const invalidLines = useMemo(
    () => preview.filter((p) => p.issues.length > 0),
    [preview],
  );
  const canSubmit =
    !submitting &&
    lines.length > 0 &&
    collectionName.trim().length > 0 &&
    invalidLines.length === 0;

  useEffect(() => {
    if (!currentOrg?.id || lines.length === 0) {
      setPreview([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      try {
        const result: { lines?: PreviewLine[] } = await fetcherAuth(
          getToken,
          `/api/organization/${currentOrg.id}/bento-brands/upload/preview`,
          "POST",
          {},
          { lines },
        );
        setPreview(result.lines ?? []);
      } catch {
        // Preview is advisory — leave existing state and let the submit
        // attempt surface any real backend error.
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [lines, currentOrg?.id, getToken]);

  const handleFileSelected = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const contents = typeof reader.result === "string" ? reader.result : "";
      setText((prev) =>
        prev ? `${prev.replace(/\s+$/, "")}\n${contents}` : contents,
      );
    };
    reader.onerror = () =>
      setErrorAlert({ message: "Could not read the file" });
    reader.readAsText(file);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !currentOrg?.id) return;
    setSubmitting(true);
    try {
      const result: UploadResult = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg.id}/bento-brands/upload`,
        "POST",
        {},
        { lines },
      );
      const unmatched = result.unmatched ?? [];
      addUnmatchedToWishlist(unmatched);
      await onUploaded({
        brands: result.brands ?? [],
        unmatched,
        optedOutContacts: result.optedOutContacts ?? [],
        wishlistedBrands: result.wishlistedBrands ?? [],
      });
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setSubmitting(false);
    }
  };

  const addUnmatchedToWishlist = (unmatched: string[]) => {
    if (unmatched.length === 0) return;
    const existing = new Set(
      userRequests(SavedBrandStatus.unsent).map(
        (r) => r.bentoBrandIdOrUserRequestDescription,
      ),
    );
    const seen = new Set<string>();
    const newRequests: CombinedRequest[] = [];
    for (const value of unmatched) {
      if (!value || existing.has(value) || seen.has(value)) continue;
      seen.add(value);
      newRequests.push({
        bentoBrandIdOrUserRequestDescription: value,
        userRequestStatus: RequestStatus.pending,
      });
    }
    if (newRequests.length === 0) return;
    setUserRequests(SavedBrandStatus.unsent)((prev) => [
      ...newRequests,
      ...prev,
    ]);
    setTotal((prev) => ({
      ...prev,
      unsent: (prev.unsent ?? 0) + newRequests.length,
    }));
  };

  if (submitting) {
    return <LoadingScreen />;
  }

  return (
    <Box sx={styles.root}>
      <Box sx={styles.header}>
        {!onClose && (
          <Button
            disableRipple
            sx={styles.backButton}
            onClick={() => setDiscoverTab(DiscoverTabView.SAVED_BRANDS)}
          >
            <Box
              component="i"
              className="fa-regular fa-arrow-left"
              sx={styles.backIcon}
            />
            Saved lists
          </Button>
        )}
        <TextField
          variant="standard"
          value={collectionName}
          onChange={(e) => setCollectionName(e.target.value)}
          inputProps={{ "aria-label": "Pitch list name", maxLength: 80 }}
          InputProps={{ disableUnderline: true, sx: styles.title }}
        />
        <IconButton
          sx={styles.closeButton}
          onClick={() => (onClose ? onClose() : setDiscoverTab(null))}
        >
          <Box
            component="i"
            className="fa-regular fa-xmark"
            sx={styles.closeIcon}
          />
        </IconButton>
      </Box>

      <Box sx={styles.body}>
        <Box sx={styles.infoBox}>
          <Typography sx={styles.infoTitle}>
            Each line can include any of these, separated by commas:
          </Typography>
          <Box sx={styles.infoRows}>
            <Box sx={styles.infoRow}>
              <Box sx={styles.exampleIcon}>
                <Box
                  component="i"
                  className="fa-brands fa-instagram"
                  sx={styles.instagramIcon}
                />
              </Box>
              <Box component="span" sx={styles.chip}>
                @glossier
              </Box>
              <Typography sx={styles.exampleLabel}>
                — Instagram handle
              </Typography>
            </Box>
            <Box sx={styles.infoRow}>
              <Box sx={styles.exampleIcon}>
                <Box
                  component="i"
                  className="fa-regular fa-globe"
                  sx={styles.globeIcon}
                />
              </Box>
              <Box component="span" sx={styles.chip}>
                fentybeauty.com
              </Box>
              <Typography sx={styles.exampleLabel}>— Brand website</Typography>
            </Box>
            <Box sx={styles.infoRow}>
              <Box sx={styles.exampleIcon}>
                <Box
                  component="i"
                  className="fa-solid fa-building"
                  sx={styles.buildingIcon}
                />
              </Box>
              <Box component="span" sx={styles.chip}>
                Drunk Elephant
              </Box>
              <Typography sx={styles.exampleLabel}>— Brand name</Typography>
            </Box>
          </Box>
          <Typography sx={styles.infoFooter}>
            You can also add an email "
            <Box component="span" className="mono">
              , hello@brand.com
            </Box>
            " after any brand if you have a contact
          </Typography>
        </Box>

        <TextField
          multiline
          minRows={9}
          maxRows={9}
          placeholder={PLACEHOLDER}
          sx={styles.textarea}
          value={text}
          onChange={(e) => setText(e.target.value)}
          InputProps={{ disableUnderline: true }}
        />

        {invalidLines.length > 0 && (
          <Box sx={styles.issuesBox}>
            <Typography sx={styles.issuesTitle}>
              Fix {invalidLines.length}{" "}
              {invalidLines.length === 1 ? "line" : "lines"} to continue:
            </Typography>
            {(issuesExpanded
              ? invalidLines
              : invalidLines.slice(0, ISSUES_PREVIEW_COUNT)
            ).map((line) => (
              <Box key={line.lineNumber} sx={styles.issueRow}>
                <Box sx={styles.issueHeader}>
                  <Box component="span" sx={styles.issueLineNum}>
                    Line {line.lineNumber}
                  </Box>
                  <Typography sx={styles.issueContent}>
                    {line.cleaned}
                  </Typography>
                </Box>
                {line.issues.map((issue) => (
                  <Typography key={issue.code} sx={styles.issueMessage}>
                    {issue.message}
                  </Typography>
                ))}
              </Box>
            ))}
            {invalidLines.length > ISSUES_PREVIEW_COUNT && (
              <Box
                component="button"
                type="button"
                sx={styles.issuesToggle}
                onClick={() => setIssuesExpanded((v) => !v)}
              >
                {issuesExpanded
                  ? "Show less"
                  : `View ${invalidLines.length - ISSUES_PREVIEW_COUNT} more`}
              </Box>
            )}
          </Box>
        )}

        <Box sx={styles.metaRow}>
          <Button
            disableRipple
            sx={styles.attachButton}
            onClick={() => fileInputRef.current?.click()}
          >
            <Box sx={styles.attachIconWrapper}>
              <Box
                component="i"
                className="fa-regular fa-upload"
                sx={styles.attachIcon}
              />
            </Box>
            Attach CSV or .txt file
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt,text/csv,text/plain"
            hidden
            onChange={handleFileSelected}
          />
          <Typography sx={styles.countLabel}>
            {lines.length} {lines.length === 1 ? "brand" : "brands"}
          </Typography>
        </Box>
      </Box>

      <Box sx={styles.footer}>
        <Button
          disabled={!canSubmit}
          sx={styles.findBrandsButton}
          onClick={handleSubmit}
        >
          <Box
            component="i"
            className="fa-regular fa-magnifying-glass"
            sx={styles.findBrandsIcon}
          />
          Find Brands
        </Button>
      </Box>
    </Box>
  );
};

export default UploadForm;
